import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Mail, 
  Calendar, 
  Send, 
  CheckCircle,
  AlertCircle,
  Clock,
  Users,
  FileText,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import GradeReportDownload from './GradeReportDownload';

const GradeReportsTab = () => {
  const [milestoneDates, setMilestoneDates] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingWeek, setSendingWeek] = useState(null);

  useEffect(() => {
    fetchMilestoneDates();
    fetchReports();
  }, []);

  const fetchMilestoneDates = async () => {
    try {
      const response = await axios.get('/academic-years/milestone-dates');
      setMilestoneDates(response.data);
    } catch (error) {
      console.error('Error fetching milestone dates:', error);
      if (error.response?.status !== 404) {
        toast.error('Error al cargar fechas milestone');
      }
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get('/grade-reports');
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const sendReportsForWeek = async (weekNumber) => {
    setSendingWeek(weekNumber);
    setLoading(true);

    try {
      const response = await axios.post(`/grade-reports/send/${weekNumber}`);
      
      toast.success(
        `✅ ${response.data.reports_sent} reportes enviados para semana ${weekNumber}`,
        { duration: 5000 }
      );

      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(error => {
          toast.warning(error, { duration: 5000 });
        });
      }

      fetchReports();
    } catch (error) {
      console.error('Error sending reports:', error);
      toast.error(`Error al enviar reportes: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
      setSendingWeek(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getReportsCountForWeek = (weekNumber) => {
    return reports.filter(r => r.week_number === weekNumber).length;
  };

  const isDatePassed = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const getMilestoneStatus = (weekNumber) => {
    if (!milestoneDates?.milestone_dates) return 'pending';
    
    const dateKey = `week_${weekNumber}`;
    const milestoneDate = milestoneDates.milestone_dates[dateKey];
    
    if (!milestoneDate) return 'pending';
    
    const reportsSent = getReportsCountForWeek(weekNumber);
    const isPassed = isDatePassed(milestoneDate);
    
    if (reportsSent > 0) return 'sent';
    if (isPassed) return 'missed';
    return 'pending';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Enviado' },
      missed: { color: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Pendiente' },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Programado' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  if (!milestoneDates) {
    return (
      <div className="space-y-6">
        <GradeReportDownload />
        <Alert>
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            No hay un año académico activo configurado. Por favor, crea un año académico primero.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GradeReportDownload />
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Reportes de Calificaciones Automáticos
        </h2>
        <p className="text-gray-600">
          Gestiona el envío automático de reportes de calificaciones a los padres
        </p>
      </div>

      {/* Academic Year Info */}
      <Card className="modern-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Año Académico Actual
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Año Académico</p>
              <p className="font-semibold">{milestoneDates.academic_year}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Fecha Inicio</p>
              <p className="font-semibold">{formatDate(milestoneDates.start_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Fecha Fin</p>
              <p className="font-semibold">{formatDate(milestoneDates.end_date)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Milestone Dates */}
      <Card className="modern-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Fechas de Envío de Reportes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[10, 20, 30, 40].map((weekNumber) => {
              const dateKey = `week_${weekNumber}`;
              const milestoneDate = milestoneDates.milestone_dates[dateKey];
              const status = getMilestoneStatus(weekNumber);
              const reportCount = getReportsCountForWeek(weekNumber);

              return (
                <div
                  key={weekNumber}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">Semana {weekNumber}</h3>
                      {getStatusBadge(status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(milestoneDate)}</span>
                      </div>
                      {reportCount > 0 && (
                        <div className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          <span>{reportCount} reportes enviados</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={() => sendReportsForWeek(weekNumber)}
                    disabled={loading || sendingWeek === weekNumber}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sendingWeek === weekNumber ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar Reportes
                      </>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Reports */}
      <Card className="modern-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Historial de Envíos Recientes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No se han enviado reportes todavía</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3">Semana</th>
                    <th className="text-left p-3">Fecha de Envío</th>
                    <th className="text-left p-3">Total Reportes</th>
                    <th className="text-left p-3">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {[10, 20, 30, 40].map((weekNumber) => {
                    const weekReports = reports.filter(r => r.week_number === weekNumber);
                    if (weekReports.length === 0) return null;

                    const successCount = weekReports.filter(r => r.sent_successfully).length;
                    const failedCount = weekReports.length - successCount;
                    const latestReport = weekReports.sort((a, b) => 
                      new Date(b.report_date) - new Date(a.report_date)
                    )[0];

                    return (
                      <tr key={weekNumber} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">Semana {weekNumber}</td>
                        <td className="p-3">{formatDate(latestReport.report_date)}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-green-600 font-semibold">{successCount}</span>
                            {failedCount > 0 && (
                              <span className="text-red-600">({failedCount} fallidos)</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          {failedCount === 0 ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completo
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Parcial
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Nota:</strong> Los reportes se envían automáticamente en las fechas configuradas. 
          También puedes enviarlos manualmente usando los botones de arriba. Asegúrate de tener 
          configurado el servidor SMTP en la sección de Configuración.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default GradeReportsTab;
